import {
  checkEmail,
  checkMax,
  checkMaxLength,
  checkMin,
  checkMinLength,
  checkPhoneNumber,
  composeValidators,
  formatCurrentDate,
  i18,
  numberFormat,
  required,
  requiredAny,
  requiredIfNot,
  requiredNotUndefined,
  translationGroups,
  checkClientBenefShare
} from "@utilities";
import { FormFieldType } from "@components/FormField";
import { CLASSIFICATOR_INSTTTNL_SCTR_ALLOWED } from "@config/translationOverrides";

export const getMapDataObject = (
  classificators,
  classificatorsConst,
  applicationCompany,
  prefillData,
  initialData
) => {
  const classifikatorsMap = (type, withSort = false, customSort = false, noZero = false) => {
    let obj = classificators?.[type],
      newObj = classificatorsConst?.[type];
    const simpleSortFunc = (a, b) => {
        return a.label > b.label ? 1 : a.label < b.label ? -1 : 0;
      },
      objectMap = obj =>
        withSort
          ? Object.entries(obj)
              .map(item => {
                return item[0] && item[1]
                  ? { label: item[1], value: item[0] }
                  : item[0]
                  ? { label: item[0], value: item[0] }
                  : null;
              })
              .filter(item => item)
              .sort(customSort ? customSort : simpleSortFunc)
          : Object.entries(obj)
              .map(item => {
                return item[0] && item[1]
                  ? { label: item[1], value: item[0] }
                  : item[0]
                  ? { label: item[0], value: item[0] }
                  : null;
              })
              .filter(item => item)
              .sort(simpleSortFunc);

    if (noZero) {
      obj = removeZero(obj);
      newObj = removeZero(newObj);
    }

    return obj ? objectMap(obj) : newObj ? objectMap(newObj) : [];
  };

  const getEnumFromPrefiledData = (enumArray = []) => {
    if (enumArray.length) {
      return enumArray.map(item => {
        return { label: item.companyDescription, value: item.companyCode };
      });
    }
    return null;
  };

  const customSortCisoFunc = (a, b) => {
    const order = ["LT", "LV", "EE", ".*"];
    const aI = order.findIndex(element => a.value.match(element));
    const bI = order.findIndex(element => b.value.match(element));
    return aI - bI;
  };

  const customSortFunc = (a, b) => {
    if (a.label.length > b.label.length) return 1;
    if (a.label.length < b.label.length) return -1;
    return a.label > b.label ? 1 : a.label < b.label ? -1 : 0;
  };

  const customSortByValueString = (a, b) => {
    return a.value.toUpperCase() > b.value.toUpperCase()
      ? 1
      : a.value.toUpperCase() < b.value.toUpperCase()
      ? -1
      : 0;
  };

  const removeZero = values => {
    if (!values) return;
    const newValues = values;
    Object.keys(values)
      .filter(key => values[key] == "0")
      .forEach(item => delete newValues[item]);
    return newValues;
  };

  const parsePhone = value => {
    if (!value) return value;
    const onlyNums = value.replace(/[^\d]/g, "");
    return `+${onlyNums}`;
  };

  const country =
    applicationCompany?.country?.substr(0, 2) ||
    prefillData?.company?.country?.substr(0, 2) ||
    "LT";

  const CISO = classifikatorsMap("CISO", true, customSortCisoFunc),
    PHON = classifikatorsMap("PHON"),
    MAILL = classifikatorsMap("MAILL"),
    DOCSALL = classifikatorsMap("DOCS").filter(
      item => item.label !== "DOCSA8" && item.label !== "DOCSA9" && item.label !== "undefined"
    ),
    DOCS = classifikatorsMap("DOCS").filter(
      item => item.value === "DOCS1" || item.value === "DOCS6"
    ),
    PEN = classifikatorsMap("PEN"),
    LGL_FRM =
      getEnumFromPrefiledData(prefillData?.companyTypesList?.companyTypes) ||
      classifikatorsMap("LGL_FRM"),
    CBB = classifikatorsMap("CBB"),
    CSIZE = classifikatorsMap("CSIZE"),
    INCPM = classifikatorsMap("INCPM", true, customSortByValueString, true),
    INCRM = classifikatorsMap("INCRM", true, customSortByValueString, true),
    OUTPM = classifikatorsMap("OUTPM", true, customSortByValueString, true),
    OUTRM = classifikatorsMap("OUTRM", true, customSortByValueString, true),
    CFUSC = classifikatorsMap("CFUSC", true, customSortFunc),
    CUREX = classifikatorsMap("CUREX", true, customSortByValueString),
    LNAM = classifikatorsMap("LNAM", true, customSortByValueString),
    MCA = classifikatorsMap("MCA", true, customSortByValueString, true),
    CCHAN = classifikatorsMap("CCHAN").filter(item => item.value !== "CCHAN4"),
    INSTTTNL_SCTR = classifikatorsMap("INSTTTNL_SCTR").filter(translation =>
      CLASSIFICATOR_INSTTTNL_SCTR_ALLOWED.includes(translation.value)
    ),
    CFURC = classifikatorsMap("CFURC", true, customSortFunc),
    CBP = classifikatorsMap("CBP"),
    BENEF = classifikatorsMap("BENEF"),
    PEPLIST = classifikatorsMap("PEPLIST").filter(
      item => item.value === "PEPLIST1" || item.value === "PEPLIST2"
    ),
    SWC = classifikatorsMap("SWC", true, customSortFunc),
    CPFSCH = classifikatorsMap("CPFSCH", true, customSortByValueString),
    CPFSC = classifikatorsMap("CPFSC", true, customSortByValueString),
    CMPNOCEO = classifikatorsMap("CMP").filter(item => item.value !== "CMP1");

  const yesNo = [{ label: "Yes", value: 1 }, { label: "No", value: 0 }];
  return [
    {
      title: i18.Labels.ClientProfile,
      rows: [
        {
          title: i18.Labels.ClientProfile,
          elements: [
            {
              title: i18.Labels.ClientId,
              name: "clientProfile.0.data.clientId",
              component: FormFieldType.input,
              className: "col-12",
              disabled: true,
              validate: composeValidators(required, checkMinLength(4), checkMaxLength(35))
            },
            {
              title: i18.Labels.ClientTitle,
              name: "clientProfile.0.data.clientTitle",
              component: FormFieldType.input,
              className: "col-12",
              disabled: true,
              validate: required
            },
            {
              title: i18.Labels.BrandName,
              classNameTitle: "form-field-title-mobile-multi-row col-12",
              name: "clientProfile.0.data.brandName",
              component: FormFieldType.input,
              className: "col-12"
            },
            {
              title: i18.Labels.ClientWeb,
              classNameTitle: "form-field-title-mobile-multi-row col-12",
              name: "clientProfile.0.data.clientWeb",
              component: FormFieldType.input,
              className: "col-12"
            },
            {
              title: i18.Labels.ClientFullName,
              name: "clientProfile.0.data.clientFullName",
              component: FormFieldType.input,
              className: "col-12 d-none"
            },
            {
              title: i18.Labels.ClientLegalStatus,
              name: "clientProfile.0.data.clientLegalStatus",
              options: LGL_FRM,
              optionsLabel: "label",
              optionsValue: "value",
              component: FormFieldType.selectSearch,
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.ClientBirthDate,
              name: "clientProfile.0.data.clientBirthDate",
              component: FormFieldType.date,
              className: "col-12",
              validate: required,
              maxDate: formatCurrentDate()
            },
            {
              title: i18.Labels.ClientLeiCode,
              name: "clientProfile.0.data.leiCode",
              component: FormFieldType.input,
              className: "col-12 d-none"
            },
            {
              title: i18.Labels.ClientRegCountry,
              name: "clientProfile.0.data.clientRegCountry",
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              component: FormFieldType.selectSearch,
              className: "col-12",
              validate: required,
              initialValue: initialData?.clientProfile?.[0]?.data?.clientRegCountry || country
            }
          ]
        },
        {
          title: i18.Labels.ClientAddress,
          className: "col-12",
          elements: [
            {
              title: i18.Labels.AddressType,
              name: "clientAddress.0.addressType",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none",
              initialValue: "ADRT2"
            },
            {
              title: i18.Labels.AddressCountry,
              name: "clientAddress.0.data.addressCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required,
              initialValue:
                initialData?.clientAddress?.[0]?.data?.addressCountry ||
                applicationCompany?.country ||
                "LT"
            },
            {
              title: i18.Labels.AddressCity,
              name: "clientAddress.0.data.addressCity",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.AddressStreet,
              name: "clientAddress.0.data.addressStreet",
              component: FormFieldType.input,
              className: "col-12 col-md-8",
              validate: required
            },
            {
              title: i18.Labels.AddressHouse,
              name: "clientAddress.0.data.addressHouse",
              component: FormFieldType.input,
              className: "col-12 col-md-4 pr-0 pr-md-3 d-none"
            },
            {
              title: i18.Labels.AddressFlat,
              name: "clientAddress.0.data.addressFlat",
              component: FormFieldType.input,
              className: "col-12 col-md-4 px-0 px-md-3 d-none"
            },
            {
              title: i18.Labels.AddressPostCode,
              name: "clientAddress.0.data.addressPostCode",
              component: FormFieldType.input,
              className: "col-12 col-md-4",
              maxLength: 8,
              validate: composeValidators(checkMaxLength(8), required)
            }
          ]
        },
        {
          title: i18.Labels.ClientSecondAddress,
          className: "col-12",
          elements: [
            {
              translate: i18.Labels.ClientSameRealAddress,
              name: "clientAddress.0.clientSameRealAddress",
              component: FormFieldType.checkboxOnly,
              className:
                "clientSameAddress-old col-12 col-md-7 offset-md-5 position-absolute justify-content-end"
            },
            {
              title: i18.Labels.AddressType,
              name: "clientAddress.1.addressType",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none",
              initialValue: "ADRT1"
            },
            {
              title: i18.Labels.AddressCountry,
              name: "clientAddress.1.data.addressCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required,
              initialValue:
                initialData?.clientAddress?.[0]?.data?.addressCountry ||
                applicationCompany?.country ||
                "LT"
            },
            {
              title: i18.Labels.AddressCity,
              name: "clientAddress.1.data.addressCity",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.AddressStreet,
              name: "clientAddress.1.data.addressStreet",
              component: FormFieldType.input,
              className: "col-12 col-md-8",
              validate: required
            },
            {
              title: i18.Labels.AddressHouse,
              name: "clientAddress.1.data.addressHouse",
              component: FormFieldType.input,
              className: "col-12 col-md-4 pr-0 pr-md-3 d-none"
            },
            {
              title: i18.Labels.AddressFlat,
              name: "clientAddress.1.data.addressFlat",
              component: FormFieldType.input,
              className: "col-12 col-md-4 px-0 px-md-3 d-none"
            },
            {
              title: i18.Labels.AddressPostCode,
              name: "clientAddress.1.data.addressPostCode",
              component: FormFieldType.input,
              className: "col-12 col-md-4",
              maxLength: 8,
              validate: composeValidators(checkMaxLength(8), required)
            }
          ]
        },
        {
          className: "col-12 col-md-6 my-0",
          elements: [
            {
              // COMMENT: this input is not shown
              title: i18.Labels.ClientPhoneType,
              name: "clientPhone.0.clientPhoneType",
              component: FormFieldType.selectSearch,
              options: PHON,
              optionsLabel: "label",
              optionsValue: "value",
              optionsDisabled: "disabled",
              className: "col-12 col-md-4 d-none"
            },
            {
              title: i18.Labels.ClientPhoneNo,
              name: "clientPhone.0.data.clientPhoneNo",
              component: FormFieldType.input,
              className: "col-12",
              maxLength: 20,
              parseFunc: parsePhone,
              validate: composeValidators(
                required,
                checkMinLength(8),
                checkMaxLength(20),
                checkPhoneNumber
              )
            }
          ]
        },
        {
          className: "col-12 col-md-6 my-0",
          elements: [
            {
              // COMMENT: not seen in old form
              title: i18.Labels.ClientEmailType,
              name: "clientMail.0.clientEmailType",
              component: FormFieldType.selectSearch,
              optionsLabel: "value",
              optionsValue: "value",
              options: MAILL,
              className: "col-12 col-md-4 d-none"
            },
            {
              title: i18.Labels.ClientEmail,
              name: "clientMail.0.data.clientEmail",
              component: FormFieldType.input,
              className: "col-12",
              validate: composeValidators(required, checkEmail)
            }
          ]
        },
        {
          className: "col-12",
          elements: [
            {
              name: "clientTaxPayerCountrys.0.clientTaxPayerCountryType",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none",
              initialValue: "TAXC1"
            },
            {
              title: i18.Labels.ClientTaxPayerCountry,
              name: "clientTaxPayerCountrys.0.data.clientTaxPayerCountry",
              component: FormFieldType.selectSearch,
              optionsLabel: "label",
              optionsValue: "value",
              options: CISO,
              className: "col-12 col-md-6",
              validate: required,
              initialValue:
                initialData?.clientTaxPayerCountrys?.[0]?.data?.clientTaxPayerCountry || country
            },
            {
              title: i18.Labels.ClientTaxPayerId,
              classNameTitle: "form-field-title-multi-row form-field-title-mobile-multi-row col-12",
              name: "clientTaxPayerCountrys.0.data.clientTaxPayerId",
              component: FormFieldType.input,
              className: "col-12",
              validate: requiredIfNot("clientTaxPayerCountrys.0.data.clientTaxPayerCountry", "LT")
            }
          ]
        }
      ]
    },
    {
      title: i18.Labels.ClientDocuments,
      rows: [
        {
          title: i18.Labels.ClientDocuments,
          className: "col-12 d-none",
          elements: [
            {
              translate: i18.Labels.OrgStructure,
              name: "documentList.0.data.orgStructure",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            },
            {
              translate: i18.Labels.Regulatons,
              name: "documentList.0.data.regulatons",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            },
            {
              translate: i18.Labels.RcList,
              name: "documentList.0.data.rcList",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            },
            {
              translate: i18.Labels.FinRaport,
              name: "documentList.0.data.finRaport",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            },
            {
              translate: i18.Labels.CeoDocument,
              name: "documentList.0.data.ceoDocument",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            },
            {
              translate: i18.Labels.CeoId,
              name: "documentList.0.data.ceoId",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            },
            {
              translate: i18.Labels.BenefDocuments,
              name: "documentList.0.data.benefDocuments",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            },
            {
              translate: i18.Labels.OtherDoc,
              name: "documentList.0.data.otherDoc",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            }
          ]
        },
        {
          title: i18.Labels.ClientPlanServices,
          className: "col-12 col-md-6",
          elements: [
            {
              translate: i18.Labels.ClientPlanServicesCard,
              name: "clientPlanServices.0.data.clientPlanServicesCard",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2",
              validate: required
            },
            {
              translate: i18.Labels.ClientPlanServicesTransf,
              name: "clientPlanServices.0.data.clientPlanServicesTransf",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPlanServicesCredSb,
              name: "clientPlanServices.0.data.clientPlanServicesCredSb",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPlanServicesDep,
              name: "clientPlanServices.0.data.clientPlanServicesDep",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPlanServicesEb,
              name: "clientPlanServices.0.data.clientPlanServicesEb",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2 d-none"
            },
            {
              name: "clientPlanServices.0.data.bankAccType",
              component: FormFieldType.input,
              className: "col-12 d-none"
            },
            {
              name: "clientPlanServices.0.clientPlanServicesType",
              component: FormFieldType.input,
              className: "col-12 d-none"
            }
          ]
        },
        {
          title: i18.Labels.CompanyFunds,
          className: "col-12 col-md-6",
          elements: [
            {
              //COMMENT WHAT THIS DOES
              title: i18.Labels.ClientCeoDocCountry,
              name: "companyFunds.0.companyFunds",
              component: FormFieldType.selectSearch,
              options: PEN,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-6 d-none",
              initialValue: "PEN1"
            },
            {
              translate: i18.Labels.CompanyFundsShareholderCap,
              name: "companyFunds.0.data.shareholderCap",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.CompanyFundsActivityProfit,
              name: "companyFunds.0.data.activityProfit",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.CompanyFundsSaleProperty,
              name: "companyFunds.0.data.saleProperty",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.CompanyFundsIncomInvest,
              name: "companyFunds.0.data.incomInvest",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.CompanyFundsSubsidies,
              name: "companyFunds.0.data.subsidies",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.CompanyFundsBorroving,
              name: "companyFunds.0.data.borroving",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.CompanyFundsOther,
              name: "companyFunds.0.data.other",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              name: "companyFunds.0.companyFundsValid",
              component: FormFieldType.checkboxOnly,
              validate: requiredAny,
              className: "col-12 only-error text-center m-2"
            }
          ]
        },
        {
          showWhen: "companyFunds.0.data.other",
          className: "col-12 my-0",
          showIs: true,
          elements: [
            {
              title: i18.Labels.CompanyFundsOther,
              name: "companyFunds.0.data.otherInput",
              component: FormFieldType.input,
              className: "col-12",
              validate: required
            }
          ]
        }
      ]
    },
    {
      title: i18.Labels.ClientCeoRep,
      className: "col-12",
      rows: [
        {
          className: "col-12 mt-sm-0 mt-md-5 mt-xl-0 mb-0",
          showWhen: "clientProfile.0.data.whoOpenAccount",
          showIs: "CMP1",
          elements: [
            {
              title: i18.Labels.ClientRepPosition,
              name: "clientProfile.0.data.clientRepPosition",
              component: FormFieldType.input,
              className: "col-12 mb-sm-5 mb-md-0 mb-xl-5",
              validate: required
            }
          ]
        },
        {
          showWhen: "clientProfile.0.data.whoOpenAccount",
          showIs: "CMP2CMP3CMP4",
          className: "col-12 mt-sm-0 mt-md-5 mt-xl-0 mb-0",
          elements: [
            {
              title: i18.Labels.WhoOpenAccountIfNotCEO,
              name: "clientProfile.0.data.whoOpenAccount",
              component: FormFieldType.selectSearch,
              options: CMPNOCEO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required,
              initialValue: "CMP2"
            }
          ]
        },
        {
          title: i18.Labels.ClientCeo,
          className: "col-12 mt-0",
          elements: [
            {
              title: i18.Labels.ClientCeoName,
              name: "clientCeo.0.data.clientCeoName",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientSurname,
              name: "clientCeo.0.data.clientCeoSurname",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientId,
              name: "clientCeo.0.data.clientCeoId",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: composeValidators(required, checkMinLength(4), checkMaxLength(35))
            },
            {
              title: i18.Labels.ClientCeoCitizien,
              name: "clientCeo.0.data.clientCeoCitizien",
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              component: FormFieldType.selectSearch,
              className: "col-12 col-md-6",
              validate: required,
              initialValue: initialData?.clientCeo?.[0]?.data?.clientCeoCitizien || country
            },
            {
              title: i18.Labels.ClientCeoBirthDate,
              name: "clientCeo.0.data.managerBirthDate",
              component: FormFieldType.date,
              className: "col-12 col-md-6",
              validate: required,
              maxDate: formatCurrentDate(),
              noInline: true
            },
            {
              title: i18.Labels.OtherCeoCitizien,
              name: "clientCeo.0.data.otherCeoCitizien",
              component: FormFieldType.radio,
              options: yesNo,
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12 mb-0",
              validate: requiredNotUndefined
            }
          ]
        },
        {
          className: "col-12",
          showWhen: "clientCeo.0.data.otherCeoCitizien",
          showIs: 1,
          elements: [
            {
              title: i18.Labels.OtherCeoCitDocTy,
              name: "clientCeo.0.data.otherCeoCitDocTy",
              options: DOCSALL,
              optionsLabel: "label",
              optionsValue: "value",
              component: FormFieldType.selectSearch,
              className: "col-12",
              validate: required,
              initialValue: "DOCS6"
            },
            {
              title: i18.Labels.OtherCeoCitDocNo,
              name: "clientCeo.0.data.otherCeoCitDocNo",
              component: FormFieldType.input,
              className: "col-12",
              maxLength: 20,
              validate: composeValidators(required, checkMaxLength(20)),
              noInline: true
            },
            {
              title: i18.Labels.OtherCeoCitDocCountry,
              name: "clientCeo.0.data.otherCeoCitDocCountry",
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              component: FormFieldType.selectSearch,
              className: "col-12",
              validate: required,
              initialValue: initialData?.clientCeo?.[0]?.data?.otherCeoCitDocCountry || country
            },
            {
              title: i18.Labels.OtherCeoCitizienSch,
              name: "clientCeo.0.data.otherCeoCitizienSch",
              component: FormFieldType.radio,
              options: yesNo,
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12",
              validate: requiredNotUndefined
            }
          ]
        },
        {
          title: i18.Labels.ClientCeoAdrress,
          className: "col-12 mb-0",
          elements: [
            {
              title: i18.Labels.ClientCeoCountry,
              name: "clientCeo.0.data.clientCeoCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required,
              initialValue: initialData?.clientCeo?.[0]?.data?.clientCeoCountry || country
            },
            {
              title: i18.Labels.ClientCeoCity,
              name: "clientCeo.0.data.clientCeoCity",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientCeoStreet,
              name: "clientCeo.0.data.clientCeoStreet",
              component: FormFieldType.input,
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.ClientCeoHouse,
              name: "clientCeo.0.data.clientCeoHouse",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none"
            },
            {
              title: i18.Labels.ClientCeoFlat,
              name: "clientCeo.0.data.clientCeoFlat",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none"
            }
          ]
        },
        {
          className: "col-12 mt-0",
          elements: [
            {
              title: i18.Labels.ClientCeoPhone,
              name: "clientCeo.0.data.clientCeoPhone",
              component: FormFieldType.input,
              className: "col-12",
              maxLength: 20,
              parseFunc: parsePhone,
              validate: composeValidators(
                required,
                checkMinLength(8),
                checkMaxLength(20),
                checkPhoneNumber
              )
            },
            {
              title: i18.Labels.CeoActDoc,
              name: "clientCeo.0.data.ceoActDoc",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none"
            },
            {
              title: i18.Labels.ClientCeoIdDocType,
              name: "clientCeo.0.data.clientCeoIdDocType",
              component: FormFieldType.selectSearch,
              options: DOCS,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required,
              noInline: true,
              initialValue: "DOCS6"
            },
            {
              title: i18.Labels.ClientCeoDocNo,
              name: "clientCeo.0.data.clientCeoDocNo",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              maxLength: 20,
              validate: composeValidators(required, checkMaxLength(20)),
              noInline: true
            },
            {
              title: i18.Labels.ClientCeoDocCountry,
              name: "clientCeo.0.data.clientCeoDocCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required,
              initialValue: initialData?.clientCeo?.[0]?.data?.clientCeoDocCountry || country,
              noInline: true
            },
            {
              title: i18.Labels.ClientCeoDocIssueDate,
              name: "clientCeo.0.data.clientCeoDocIssueDate",
              component: FormFieldType.date,
              className: "col-12 col-md-6",
              validate: required,
              maxDate: formatCurrentDate(),
              noInline: true
            },
            {
              title: i18.Labels.ClientCeoDocValidDate,
              name: "clientCeo.0.data.clientCeoDocValidDate",
              component: FormFieldType.date,
              className: "col-12 col-md-6",
              validate: required,
              minDate: formatCurrentDate(),
              noInline: true
            },
            {
              title: i18.Labels.ClientCeoDocIssuePlace,
              name: "clientCeo.0.data.clientCeoDocIssuePlace",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none"
            }
          ]
        },
        {
          title: i18.Labels.ClientRep,
          className: "col-12 mt-md-5",
          showWhen: "clientProfile.0.data.whoOpenAccount",
          showIs: "CMP2CMP3CMP4",
          elements: [
            {
              title: i18.Labels.ClientRepName,
              name: "clientRep.0.data.clientRepName",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientRepSurname,
              name: "clientRep.0.data.clientRepSurname",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientRepId,
              name: "clientRep.0.data.clientRepId",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: composeValidators(required, checkMinLength(4), checkMaxLength(35))
            },
            {
              title: i18.Labels.ClientRepRegCountry,
              name: "clientRep.0.data.clientRepRegCountry",
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              component: FormFieldType.selectSearch,
              className: "col-12 col-md-6",
              validate: required,
              initialValue: initialData?.clientRep?.[0]?.data?.clientRepRegCountry || country
            },
            {
              title: i18.Labels.ClientRepBirthDate,
              name: "clientRep.0.data.clientRepBirthDate",
              component: FormFieldType.date,
              className: "col-12 col-md-6",
              validate: required,
              maxDate: formatCurrentDate(),
              noInline: true
            }
          ]
        },
        {
          title: i18.Labels.ClientRepAdrress,
          showWhen: "clientProfile.0.data.whoOpenAccount",
          showIs: "CMP2CMP3CMP4",
          className: "col-12 mb-0",
          elements: [
            {
              title: i18.Labels.ClientRepCountry,
              name: "clientRep.0.data.clientRepCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required,
              initialValue: initialData?.clientRep?.[0]?.data?.clientRepCountry || country
            },
            {
              title: i18.Labels.ClientRepCity,
              name: "clientRep.0.data.clientRepCity",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientRepStreet,
              name: "clientRep.0.data.clientRepStreet",
              component: FormFieldType.input,
              className: "col-12"
            },
            {
              title: i18.Labels.ClientRepHouse,
              name: "clientRep.0.data.clientRepHouse",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none"
            },
            {
              title: i18.Labels.ClientRepFlat,
              name: "clientRep.0.data.clientRepFlat",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none"
            },
            {
              title: i18.Labels.ClientRepPhoneNo,
              name: "clientRep.0.data.clientRepPhoneNo",
              component: FormFieldType.input,
              className: "col-12",
              maxLength: 20,
              parseFunc: parsePhone,
              validate: composeValidators(
                required,
                checkMinLength(8),
                checkMaxLength(20),
                checkPhoneNumber
              )
            }
          ]
        },
        {
          showWhen: "clientProfile.0.data.whoOpenAccount",
          showIs: "CMP2CMP3CMP4",
          className: "col-12 mt-0",
          elements: [
            {
              title: i18.Labels.ClientRepIdDocType,
              name: "clientRep.0.data.clientRepIdDocType",
              component: FormFieldType.selectSearch,
              options: DOCS,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required,
              noInline: true,
              initialValue: "DOCS6"
            },
            {
              title: i18.Labels.ClientRepDocCountry,
              name: "clientRep.0.data.clientRepDocCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required,
              initialValue: initialData?.clientRep?.[0]?.data?.clientRepDocCountry || country,
              noInline: true
            },
            {
              title: i18.Labels.ClientRepDocIssuePlace,
              name: "clientRep.0.data.clientRepDocIssuePlace",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none",
              noInline: true
            },
            {
              title: i18.Labels.ClientRepDocIssueDate,
              name: "clientRep.0.data.clientRepDocIssueDate",
              component: FormFieldType.date,
              className: "col-12 col-md-6",
              validate: required,
              maxDate: formatCurrentDate(),
              noInline: true
            },
            {
              title: i18.Labels.ClientRepDocNo,
              name: "clientRep.0.data.clientRepDocNo",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              maxLength: 20,
              validate: composeValidators(required, checkMaxLength(20)),
              noInline: true
            },
            {
              title: i18.Labels.ClientRepDocValidDate,
              name: "clientRep.0.data.clientRepDocValidDate",
              component: FormFieldType.date,
              className: "col-12 col-md-6",
              validate: required,
              minDate: formatCurrentDate(),
              noInline: true
            },
            {
              title: i18.Labels.ClientRepMandatNo,
              name: "clientRep.0.data.clientRepMandatNo",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none",
              noInline: true
            },
            {
              title: i18.Labels.ClientRepMandatValidDate,
              name: "clientRep.0.data.clientRepMandatValidDate",
              component: FormFieldType.date,
              className: "col-12 col-md-6",
              validate: required,
              minDate: formatCurrentDate(),
              noInline: true
            },
            {
              title: i18.Labels.ActDoc,
              name: "clientRep.0.data.actDoc",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none"
            }
          ]
        }
      ]
    },
    {
      title: i18.Labels.PoliticallyExposedPerson,
      rows: [
        {
          title: i18.Labels.PoliticallyExposedPerson,
          className: "col-12 my-0",
          elements: []
        },
        {
          messageBefore: i18.Messages.PoliticallyExposedPersonNote,
          elements: [
            {
              title: i18.Labels.ClientPoliticsMember,
              name: "clientProfile.0.data.clientPoliticsMember",
              component: FormFieldType.radio,
              options: [
                {
                  label: "True",
                  value: 1
                },
                {
                  label: "False",
                  value: 0
                }
              ],
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12",
              inline: true,
              validate: requiredNotUndefined
            }
          ]
        },
        {
          showWhen: "clientProfile.0.data.clientPoliticsMember",
          showIs: 1,
          className: "col-12 mt--5 mb-0",
          elements: [
            {
              title: i18.Labels.ClientBenefType,
              name: "clientPoliticsPosition.0.ClientPoliticsPosList",
              component: FormFieldType.input,
              className: "col-6 d-none",
              initialValue: "PEN1"
            },
            {
              translate: i18.Labels.ClientPoliticsMinist,
              name: "clientPoliticsPosition.0.data.clientPoliticsMinist",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticsJutge,
              name: "clientPoliticsPosition.0.data.clientPoliticsJutge",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticsMay,
              name: "clientPoliticsPosition.0.data.clientPoliticsMay",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticsAudit,
              name: "clientPoliticsPosition.0.data.clientPoliticsAudit",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticsMJoint,
              name: "clientPoliticsPosition.0.data.clientPoliticsMJoint",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticsAmb,
              name: "clientPoliticsPosition.0.data.clientPoliticsAmb",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticsJoint,
              name: "clientPoliticsPosition.0.data.clientPoliticsJoint",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticsInternOrg,
              name: "clientPoliticsPosition.0.data.clientPoliticsInternOrg",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticsPolPart,
              name: "clientPoliticsPosition.0.data.clientPoliticsPolPart",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticArmForces,
              name: "clientPoliticsPosition.0.data.clientPoliticArmForces",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              translate: i18.Labels.ClientPoliticOther,
              name: "clientPoliticsPosition.0.data.clientPoliticOther",
              component: FormFieldType.checkboxOnly,
              className: "col-12 my-2"
            },
            {
              name: "clientPoliticsPosition.0.clientPoliticsValid",
              component: FormFieldType.checkboxOnly,
              validate: requiredAny,
              className: "col-12 only-error text-center m-2"
            }
          ]
        },
        {
          showWhen: "clientPoliticsPosition.0.data.clientPoliticOther",
          showIs: 1,
          className: "col-12 my-0",
          elements: [
            {
              title: i18.Labels.ClientPoliticsType,
              name: "clientPoliticsPosition.0.data.clientPoliticOtherInput",
              component: FormFieldType.input,
              className: "col-12",
              validate: required
            }
          ]
        },
        {
          title: i18.Labels.ClientPoliticsList,
          showWhen: "clientProfile.0.data.clientPoliticsMember",
          showIs: 1,
          elements: [
            {
              title: i18.Labels.ClientPoliticsType,
              name: "clientPolitics.0.clientPoliticsList",
              component: FormFieldType.radio,
              options: PEPLIST,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              initialValue: "PEPLIST2",
              validate: required
            },
            {
              title: i18.Labels.ClientPoliticsName,
              name: "clientPolitics.0.data.clientPoliticsName",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientPoliticsCountry,
              name: "clientPolitics.0.data.clientPoliticsCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.PepInstitution,
              name: "clientPolitics.0.data.pepInstitution",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientPep_Id,
              name: "clientPolitics.0.data.clientPep_Id",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            }
          ]
        }
      ]
    },
    {
      title: i18.Labels.ClientBeneficiaries, // form stpe 5 starts here
      rows: [
        {
          title: i18.Labels.ClientBeneficiaries,
          noteFirst: i18.Messages.ClientBenefecariesNoteAfter,
          className: "col-12 my-0",
          elements: []
        },
        {
          elements: [
            {
              title: i18.Labels.TradeSecur,
              name: "clientBeneficiaries.0.data.tradeSecur",
              component: FormFieldType.radio,
              options: [
                {
                  label: "Yes",
                  value: 1
                },
                {
                  label: "No",
                  value: 0
                }
              ],
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12",
              inline: true,
              validate: requiredNotUndefined
            }
          ]
        },
        {
          showWhen: "clientBeneficiaries.0.data.tradeSecur",
          showIs: 1,
          elements: [
            {
              title: i18.Labels.IsinCode,
              name: "clientBeneficiaries.0.data.isinCode",
              component: FormFieldType.input,
              className: "col-12",
              inline: true
            }
          ]
        },
        {
          showWhen: "clientBeneficiaries.0.data.tradeSecur",
          showIs: 1,
          elements: [
            {
              title: i18.Labels.IsinCode,
              name: "clientBeneficiaries.0.data.isinCode",
              component: FormFieldType.input,
              className: "col-12",
              inline: true
            }
          ]
        },
        {
          showWhen: "clientBeneficiaries.0.data.tradeSecur",
          showIs: 1,
          messageBefore: i18.Messages.ClientBenefecariesNote,
          elements: []
        },
        {
          elements: [
            {
              translate: i18.Labels.Uboretail,
              name: "clientBeneficiaries.0.data.uboretail",
              component: FormFieldType.checkboxOnly,
              className: "col-12 col-md-6 mx-3"
            }
          ]
        },
        {
          canDuplicate: true,
          duplicateName: "clientBeneficiaries",
          duplicateDefault: {
            data: {},
            clientBenefType: "BENEF$index"
          },
          maxRows: BENEF.length - 1,
          elements: [
            {
              title: i18.Labels.ClientBenefType,
              name: "clientBeneficiaries.$index.clientBenefType",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none",
              initialValue: "BENEF1"
            },
            {
              title: i18.Labels.ClientBenefName,
              name: "clientBeneficiaries.$index.data.clientBenefName",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientBenefSurname,
              name: "clientBeneficiaries.$index.data.clientBenefSurname",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientBenefId,
              name: "clientBeneficiaries.$index.data.clientBenefId",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientBenefAddress,
              name: "clientBeneficiaries.$index.data.clientBenefAddress",
              component: FormFieldType.input,
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.ClientBenefRegCountry,
              name: "clientBeneficiaries.$index.data.clientBenefRegCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientBenefBirthDate,
              name: "clientBeneficiaries.$index.data.clientBenefBirthDate",
              component: FormFieldType.date,
              className: "col-12 col-md-6",
              maxDate: formatCurrentDate(),
              validate: required
            },
            {
              title: i18.Labels.ClientBenefShare,
              name: "clientBeneficiaries.$index.data.clientBenefShare",
              component: FormFieldType.number,
              className: "col-12 col-md-6",
              validate: composeValidators(required, checkMax(100), checkMin(0))
            },
            {
              title: i18.Labels.ClientBenefTaxCountry,
              name: "clientBeneficiaries.$index.data.clientBenefTaxCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.ClientBenefCountry,
              name: "clientBeneficiaries.$index.data.clientBenefCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.OtherClientBenefRegCountry,
              name: "clientBeneficiaries.$index.data.otherClientBenefRegCountry",
              component: FormFieldType.radio,
              options: yesNo,
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12",
              validate: requiredNotUndefined
            },

            {
              title: i18.Labels.OtherClientBenefRegDocType,
              name: "clientBeneficiaries.$index.data.otherClientBenefRegDocType",
              options: DOCSALL,
              optionsLabel: "label",
              optionsValue: "value",
              component: FormFieldType.selectSearch,
              className: "col-12",
              validate: required,
              initialValue: "DOCS6",
              showWhen: "clientBeneficiaries.$index.data.otherClientBenefRegCountry",
              showIs: [1]
            },
            {
              title: i18.Labels.OtherClientBenefRegDocNo,
              name: "clientBeneficiaries.$index.data.otherClientBenefRegDocNo",
              component: FormFieldType.input,
              className: "col-12",
              maxLength: 20,
              validate: composeValidators(required, checkMaxLength(20)),
              noInline: true,
              showWhen: "clientBeneficiaries.$index.data.otherClientBenefRegCountry",
              showIs: [1]
            },
            {
              title: i18.Labels.OtherClientBenefRegDocCountry,
              name: "clientBeneficiaries.$index.data.otherClientBenefRegDocCountry",
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              component: FormFieldType.selectSearch,
              className: "col-12",
              validate: required,
              initialValue: country,
              showWhen: "clientBeneficiaries.$index.data.otherClientBenefRegCountry",
              showIs: [1]
            },
            {
              title: i18.Labels.OtherClientBenefRegCountSch,
              name: "clientBeneficiaries.$index.data.otherClientBenefRegCountSch",
              component: FormFieldType.radio,
              options: yesNo,
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12",
              validate: requiredNotUndefined,
              showWhen: "clientBeneficiaries.$index.data.otherClientBenefRegCountry",
              showIs: [1]
            },
            {
              title: i18.Labels.ClientBenefTaxId,
              name: "clientBeneficiaries.$index.data.clientBenefTaxId",
              component: FormFieldType.input,
              className: "col-12"
            },
            {
              title: i18.Labels.ClientBenefEmail,
              name: "clientBeneficiaries.$index.data.clientBenefEmail",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: composeValidators(required, checkEmail)
            },
            {
              title: i18.Labels.ClientBenefIdDoc,
              name: "clientBeneficiaries.$index.data.clientBenefIdDoc",
              component: FormFieldType.selectSearch,
              options: DOCS,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.ClientBenefDocNo,
              name: "clientBeneficiaries.$index.data.clientBenefDocNo",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              maxLength: 20,
              validate: composeValidators(checkMaxLength(20))
            },
            {
              title: i18.Labels.BeneficiaryFunds,
              name: "clientBeneficiaries.$index.data.beneficiaryFunds",
              component: FormFieldType.selectSearch,
              options: SWC,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientBenefPhoneNo,
              name: "clientBeneficiaries.$index.data.clientBenefPhoneNo",
              component: FormFieldType.input,
              className: "col-12",
              maxLength: 20,
              parseFunc: parsePhone,
              validate: composeValidators(
                required,
                checkMinLength(8),
                checkMaxLength(20),
                checkPhoneNumber
              )
            }
          ]
        },
        {
          elements: [
            {
              name: "clientBeneficiaries.0.uboretail",
              component: FormFieldType.checkboxOnly,
              validate: checkClientBenefShare,
              className: "col-12 only-error text-center m-2"
            }
          ]
        }
      ]
    },
    {
      title: i18.Labels.CompanyActivity,
      rows: [
        {
          title: i18.Labels.ClientBasicBusiness,
          className: "col-12 mb-0",
          elements: [
            {
              title: i18.Labels.ClientBasicBusinessType,
              name: "clientBasicBusiness.0.clientBasicBusinessType",
              component: FormFieldType.input,
              className: "col-12 col-md-6 d-none",
              initialValue: "CBBSN1"
            },
            {
              title: i18.Labels.ClientBasicBusiness,
              name: "clientBasicBusiness.0.data.clientBasicBusiness",
              component: FormFieldType.selectSearch,
              options: CBB,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.ClientServicesDescription,
              classNameTitle: "form-field-title-mobile-multi-row col-12",
              name: "clientProfile.0.data.clientServicesDescription",
              component: FormFieldType.input,
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.RequireBusinessLicense,
              name: "clientProfile.0.data.requireBusinessLicense",
              component: FormFieldType.radio,
              options: yesNo,
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12",
              validate: requiredNotUndefined
            }
          ]
        },
        {
          showWhen: "clientProfile.0.data.requireBusinessLicense",
          className: "col-12 mt-0 mb-0",
          showIs: 1,
          elements: [
            {
              title: i18.Labels.BusinessLicenseNumber,
              name: "clientProfile.0.data.businessLicenseNumber",
              component: FormFieldType.input,
              className: "col-12 mt-0",
              validate: required
            }
          ]
        },
        {
          elements: [
            {
              title: i18.Labels.AmlRulesLeg,
              name: "clientProfile.0.data.amlRulesLeg",
              component: FormFieldType.radio,
              options: yesNo,
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12 mb-0",
              validate: requiredNotUndefined
            }
          ]
        },
        {
          showWhen: "clientProfile.0.data.amlRulesLeg",
          className: "col-12 mt-0 mb-0",
          showIs: 1,
          elements: [
            {
              title: i18.Labels.AmlProcedure,
              name: "clientProfile.0.data.amlProcedure",
              component: FormFieldType.radio,
              options: yesNo,
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12",
              validate: requiredNotUndefined
            },
            {
              title: i18.Labels.AmlCheckInSancLis,
              name: "clientProfile.0.data.amlCheckInSancLis",
              component: FormFieldType.input,
              className: "col-12",
              validate: required
            }
          ]
        },
        {
          messageBefore: i18.Labels.AmlOfficer,
          showWhen: "clientProfile.0.data.amlRulesLeg",
          className:
            "col-12 mt-0 mb-0 font-weight-normal form-field-wrapper.big-input form-field-title form-field-title",
          showIs: 1,
          elements: [
            {
              title: i18.Labels.AmlOfficerEmail,
              name: "clientProfile.0.data.amlOfficerEmail",
              component: FormFieldType.input,
              className: "col-12",
              validate: composeValidators(required, checkEmail)
            },
            {
              title: i18.Labels.AmlOfficerNameSur,
              name: "clientProfile.0.data.amlOfficerNameSur",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.AmlOfficerPhone,
              name: "clientPhone.0.data.amlOfficerPhone",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              maxLength: 20,
              parseFunc: parsePhone,
              validate: composeValidators(
                required,
                checkMinLength(8),
                checkMaxLength(20),
                checkPhoneNumber
              )
            }
          ]
        },
        {
          elements: [
            {
              title: i18.Labels.YearlyIncome,
              name: "clientBasicBusiness.0.data.yearlyIncome",
              component: FormFieldType.number,
              className: "col-12 col-md-6",
              validate: composeValidators(requiredNotUndefined, checkMin(0))
            },
            {
              title: i18.Labels.InsSect,
              name: "clientProfile.0.data.insSect",
              options: INSTTTNL_SCTR,
              component: FormFieldType.selectSearch,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.BalAsst,
              name: "clientProfile.0.data.balAsst",
              component: FormFieldType.number,
              className: "col-12",
              validate: composeValidators(requiredNotUndefined, checkMin(0))
            },
            {
              title: i18.Labels.ClientEmployeeQty,
              name: "clientProfile.0.data.clientEmployeeQty",
              component: FormFieldType.number,
              formatNumber: numberFormat().number,
              className: "col-12 col-md-6",
              validate: composeValidators(requiredNotUndefined, checkMin(0))
            },
            {
              title: i18.Labels.ClientSize,
              name: "clientProfile.0.data.clientSize",
              options: CSIZE,
              component: FormFieldType.selectSearch,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            }
          ]
        }
      ]
    },
    {
      title: i18.Labels.ClientPlanTurn,
      rows: [
        {
          className: "col-12 mb-3",
          elements: [
            /* {
              name: "clientPlanTurn.0.data.incPaym",
              component: FormFieldType.input,
              className: "col-12 d-none",
              initialValue: "PEN1"
            }, */
            {
              name: "clientPlanTurn.0.data.clientPlanTurnover",
              component: FormFieldType.input,
              className: "col-12 d-none",
              initialValue: "PLTUR1"
            },
            {
              title: i18.Labels.IncPaym,
              name: "clientPlanTurn.0.data.incPaym",
              options: INCPM,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 mb-5",
              classNameField: "col-12",
              validate: required
            },
            {
              title: i18.Labels.IncRem,
              name: "clientPlanTurn.0.data.incRem",
              options: INCRM,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              classNameField: "col-12",
              validate: required
            },
            {
              title: i18.Labels.ClientPaymCash,
              name: "clientPaymentForServises.0.data.clientPaymCash",
              options: CPFSCH,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              classNameField: "col-12",
              validate: required
            },
            {
              title: i18.Labels.ClientPaymCryp,
              name: "clientPaymentForServises.0.data.clientPaymCryp",
              options: CPFSC,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              classNameField: "col-12",
              validate: required
            }
          ]
        },
        {
          title: i18.Labels.IncomFrom,
          canDuplicate: true,
          duplicateName: "clientFundsRecCountrys",
          duplicateDefault: {
            data: {},
            clientFundsRecCountryType: "CFURC$index"
          },
          maxRows: CFURC.length - 1,
          elements: [
            {
              name: "clientFundsRecCountrys.$index.clientFundsRecCountryType",
              component: FormFieldType.input,
              className: "col-12 d-none",
              initialValue: "S.121"
            },
            {
              title: i18.Labels.ClientFundsRecCountry,
              name: "clientFundsRecCountrys.$index.data.clientFundsRecCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required
            }
          ]
        },
        {
          title: i18.Labels.MainCustom,
          className: "col-12 mb-3 mt-0",
          elements: []
        },
        {
          className: "col-6 mb-6 mt-0",
          elements: [
            {
              translate: i18.Labels.CustomerEmpty,
              name: "mainCustom.0.customerEmpty",
              component: FormFieldType.checkboxOnly,
              className: "col-12 col-md-12"
            }
          ]
        },
        {
          className: "col-6 mb-6 mt-0",
          elements: [
            {
              translate: i18.Labels.Retail,
              name: "mainCustom.0.retail",
              component: FormFieldType.checkboxOnly,
              className: "col-12 col-md-12"
            }
          ]
        },
        {
          className: "col-6 mb-6 mt-0 d-none",
          elements: [
            {
              translate: "eeempty",
              name: "mainCustom.0.checkedRetailOrEmpty",
              component: FormFieldType.checkboxOnly,
              className: "col-12 col-md-12"
            }
          ]
        },
        {
          canDuplicate: true,
          duplicateName: "mainCustom",
          duplicateDefault: {
            data: {},
            mainCustom: "PEN$index"
          },
          maxRows: PEN.length - 1,
          className: "col-12 mb-3 mt-0",
          showWhen: "mainCustom.0.checkedRetailOrEmpty",
          showIs: false,
          elements: [
            {
              name: "mainCustom.$index.mainCustom",
              component: FormFieldType.input,
              className: "col-12 d-none",
              initialValue: "PEN1"
            },
            {
              title: i18.Labels.CustomerName,
              name: "mainCustom.$index.data.customerName",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.CustomerActivity,
              name: "mainCustom.$index.data.customerActivity",
              component: FormFieldType.selectSearch,
              options: CBB,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.CustomerCountry,
              name: "mainCustom.$index.data.customerCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            }
          ]
        },
        {
          className: "col-12 mb-3",
          elements: [
            {
              name: "PaymOut.0.PaymOut",
              component: FormFieldType.input,
              className: "col-12 d-none",
              initialValue: "PEN1"
            },
            {
              title: i18.Labels.OutPaym,
              name: "PaymOut.0.data.paymOut",
              options: OUTPM,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 mb-5",
              classNameField: "col-12",
              validate: required
            },
            {
              title: i18.Labels.OutRem,
              name: "PaymOut.0.data.remOut",
              options: OUTRM,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              classNameField: "col-12",
              validate: required
            }
          ]
        },
        {
          title: i18.Labels.FundsSentTo,
          canDuplicate: true,
          duplicateName: "clientFundsSentCountry",
          duplicateDefault: {
            data: {},
            clientFundsSentCountryType: "CFUSC$index"
          },
          maxRows: CFUSC.length - 1,
          className: "col-12 mb-3",
          elements: [
            {
              name: "clientFundsSentCountry.$index.clientFundsSentCountryType",
              component: FormFieldType.input,
              className: "col-12 d-none",
              initialValue: "CFUSC1"
            },
            {
              title: i18.Labels.ClientFundsSentCountry,
              name: "clientFundsSentCountry.$index.data.clientFundsSentCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required
            }
          ]
        },
        {
          title: i18.Labels.MainBusPartner,
          className: "col-12 mb-5",
          elements: [
            {
              translate: i18.Labels.ClientBusPartnerEmpty,
              name: "clientBusPartner.0.clientBusPartnerEmpty",
              component: FormFieldType.checkboxOnly,
              className: "col-12 col-md-6 mx-3"
            }
          ]
        },
        {
          canDuplicate: true,
          duplicateName: "clientBusPartner",
          duplicateDefault: {
            data: {},
            cientBusPartnerType: "CBP$index"
          },
          maxRows: CBP.length - 1,
          showWhen: "clientBusPartner.0.clientBusPartnerEmpty",
          showIs: false,
          className: "col-12 mb-3",
          elements: [
            {
              name: "clientBusPartner.$index.cientBusPartnerType",
              component: FormFieldType.input,
              className: "col-12 col-md-6 col-md-4 d-none",
              initialValue: "S.121"
            },
            {
              title: i18.Labels.ClientBusPartnerName,
              name: "clientBusPartner.$index.data.clientBusPartnerName",
              component: FormFieldType.input,
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientBusPartnerAct,
              name: "clientBusPartner.$index.data.clientBusPartnerAct",
              component: FormFieldType.selectSearch,
              options: CBB,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            },
            {
              title: i18.Labels.ClientBusPartnerCountry,
              name: "clientBusPartner.$index.data.clientBusPartnerCountry",
              component: FormFieldType.selectSearch,
              options: CISO,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12 col-md-6",
              validate: required
            }
          ]
        },
        {
          title: i18.Labels.PlanServices,
          elements: [
            {
              name: "planServTurn.0.planServTurn",
              component: FormFieldType.input,
              className: "col-12 d-none",
              initialValue: "PEN1"
            },
            {
              title: i18.Labels.CurrExc,
              name: "planServTurn.0.data.currExchange",
              options: CUREX,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.Loan,
              name: "planServTurn.0.data.loanSum",
              options: LNAM,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required
            },
            {
              title: i18.Labels.MoneyInAcc,
              name: "planServTurn.0.data.moInAcc",
              options: MCA,
              component: FormFieldType.radio,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              validate: required
            }
          ]
        }
      ]
    },
    {
      title: i18.Labels.OtherInformation,
      rows: [
        {
          title: i18.Labels.Confirmation,
          messageBefore: i18.Messages.Confirmation,
          elements: []
        },
        {
          title: i18.Labels.PersonalDataProccessing,
          messageBefore: i18.Messages.PersonalDataProccessing,
          className: "col-12 mt--5",
          elements: []
        },
        {
          className: "col-12 mt--5",
          elements: [
            {
              title: i18.Labels.DirectMarketing,
              name: "clientProfile.0.data.directMarketing",
              component: FormFieldType.radio,
              options: yesNo,
              optionsLabel: "label",
              optionsValue: "value",
              translate: translationGroups.Labels,
              className: "col-12",
              inline: true,
              validate: requiredNotUndefined
            },
            {
              title: i18.Labels.ClientChannel,
              name: "clientProfile.0.data.clientChannel",
              component: FormFieldType.radio,
              options: CCHAN,
              optionsLabel: "label",
              optionsValue: "value",
              className: "col-12",
              inline: true,
              validate: required
            }
          ]
        }
      ]
    }
  ];
};

// export const mockData = {
//   clientProfile: [
//     {
//       clientProfileType: "CPROF7",
//       data: {
//         questStoreDate: "2021-04-23",
//         clientTitle: "Apuokai",
//         clientFullName: "UAB Apuokai",
//         clientLegalStatus: "CTIP2",
//         clientId: "30000000003",
//         clientBirthDate: "2021-03-30",
//         clientRegCountry: "LT",
//         clientWeb: "www.apuokai.lt",
//         clientRepPosition: "Direktorius",
//         whoOpenAccount: "1",
//         clientPoliticsMember: "0",
//         insSect: "303",
//         balAsst: "2000000",
//         clientEmployeeQty: "10",
//         clientSize: "CSIZE1",
//         directMarketing: "0",
//         clientChannel: "CCHAN1",
//         clientManager: " Romanas Stelmokaitis"
//       }
//     }
//   ],
//   clientAddress: [
//     {
//       addressType: "ADRT1",
//       data: {
//         addressCountry: "LT",
//         addressCity: "Vilnius",
//         addressStreet: "Vilniaus",
//         addressHouse: "23",
//         addressFlat: "23",
//         addressPostCode: "10109"
//       }
//     }
//   ],
//   clientMail: [
//     {
//       clientEmailType: "MAILL3",
//       data: {
//         clientEmail: "info@apuokai.lt"
//       }
//     }
//   ],
//   clientPhone: [
//     {
//       clientPhoneType: "Mob",
//       data: {
//         clientPhoneNo: "+37060012345"
//       }
//     }
//   ],
//   clientTaxPayerCountrys: [
//     {
//       clientTaxPayerCountryType: "TAXC1",
//       data: {
//         clientTaxPayerId: "",
//         clientTaxPayerCountry: "LT"
//       }
//     }
//   ],
//   documentList: [
//     {
//       documentList: "PEN1",
//       data: {
//         orgStructure: "1",
//         regulatons: "1",
//         rcList: "1",
//         finRaport: "",
//         ceoDocument: "",
//         ceoId: "",
//         benefDocuments: "1",
//         otherDoc: ""
//       }
//     }
//   ],
//   clientPlanServices: [
//     {
//       clientPlanServicesType: "PEN1",
//       data: {
//         clientPlanServicesCard: "1",
//         clientPlanServicesTransf: "",
//         clientPlanServicesCredSb: "",
//         clientPlanServicesDep: "",
//         clientPlanServicesEb: "",
//         bankAccType: "ACCT10"
//       }
//     }
//   ],
//   companyFunds: [
//     {
//       companyFunds: "PEN1",
//       data: {
//         shareholderCap: "1",
//         activityProfit: "",
//         saleProperty: "",
//         incomInvest: "",
//         subsidies: "",
//         borroving: "",
//         other: ""
//       }
//     }
//   ],
//   clientCeo: [
//     {
//       clientCeoType: "CEOC1",
//       data: {
//         clientCeoName: "Romanas",
//         clientCeoSurname: "Stelmokaitis",
//         clientCeoId: "38409040103",
//         clientCeoCitizien: "LT",
//         clientCeoCountry: "LT",
//         clientCeoCity: "Vilnius",
//         clientCeoStreet: "Vilniaus",
//         clientCeoHouse: "1",
//         clientCeoFlat: "2",
//         clientCeoPhone: "+37060031330",
//         clientCeoIdDocType: "DOCS6",
//         clientCeoDocCountry: "LT",
//         clientCeoDocIssuePlace: "Vilnius",
//         clientCeoDocIssueDate: "2021-04-01",
//         clientCeoDocNo: "39939339",
//         clientCeoDocValidDate: "2021-04-25",
//         ceoActDoc: "Įstatai"
//       }
//     }
//   ],
//   clientRep: [
//     {
//       clientRepType: "REPC1",
//       data: {
//         clientRepName: "",
//         clientRepSurname: "",
//         clientRepId: "",
//         clientRepRegCountry: "",
//         clientRepCountry: "",
//         clientRepCity: "",
//         clientRepStreet: "",
//         clientRepHouse: "",
//         clientRepFlat: "",
//         clientRepPhoneNo: "",
//         clientRepIdDocType: "",
//         clientRepDocCountry: "",
//         clientRepDocIssuePlace: "",
//         clientRepDocIssueDate: "",
//         clientRepDocNo: "",
//         clientRepDocValidDate: "",
//         clientRepMandatNo: "",
//         clientRepMandatValidDate: "",
//         actDoc: ""
//       }
//     }
//   ],
//   clientBeneficiaries: [
//     {
//       clientBenefType: "BENEF1",
//       data: {
//         tradeSecur: "0",
//         clientBenefName: "Romanas",
//         clientBenefSurname: "Stelmokaitis",
//         clientBenefId: "38409040103",
//         clientBenefAddress: "Vilnius",
//         clientBenefRegCountry: "LT",
//         clientBenefShare: "100",
//         clientBenefTaxCountry: "LT",
//         clientBenefTaxId: "",
//         clientBenefEmail: "romanas@bbservice.lt",
//         clientBenefPhoneNo: "+37060031330",
//         beneficiaryFunds: "SWC1"
//       }
//     }
//   ],
//   clientBasicBusiness: [
//     {
//       clientBasicBusinessType: "CBBSN1",
//       data: {
//         clientBasicBusiness: "C.27",
//         yearlyIncome: "1000000"
//       }
//     }
//   ],
//   clientPlanTurn: [
//     {
//       clientPlanTurn: "PEN1",
//       data: {
//         incPaym: "INCPM2",
//         incRem: "INCRM2",
//         clientPlanTurnover: "PLTUR1"
//       }
//     }
//   ],
//   clientFundsRecCountrys: [
//     {
//       clientFundsRecCountryType: "CFURC1",
//       data: {
//         clientFundsRecCountry: "LT"
//       }
//     }
//   ],
//   mainCustom: [
//     {
//       mainCustom: "PEN1",
//       data: {
//         customerName: "UAB Papuasai",
//         customerActivity: "N.82"
//       }
//     }
//   ],
//   PaymOut: [
//     {
//       PaymOut: "PEN1",
//       data: {
//         paymOut: "OUTPM2",
//         remOut: "OUTRM2"
//       }
//     }
//   ],
//   clientFundsSentCountry: [
//     {
//       clientFundsSentCountryType: "CFUSC1",
//       data: {
//         clientFundsSentCountry: "LT"
//       }
//     }
//   ],
//   clientBusPartner: [
//     {
//       cientBusPartnerType: "CBP1",
//       data: {
//         clientBusPartnerName: "UAB Papuasai",
//         clientBusPartnerAct: "N.80"
//       }
//     }
//   ],
//   planServTurn: [
//     {
//       planServTurn: "PEN3",
//       data: {
//         currExchange: "CUREX2",
//         loanSum: "LNAM2",
//         moInAcc: "MCA2"
//       }
//     }
//   ],
//   clientPlanCashTurn: [
//     {
//       clientPlanCashTurn: "PEN1",
//       data: {
//         clientPlanCashTurnover: "PLCASH1"
//       }
//     }
//   ]
// };
